/**
 * Generic workflow client types - mirrors the DRF serializers in
 * apps/workflows/api/serializers.py.
 *
 * These types are intentionally *resource-agnostic*; consumers wire them
 * up through the WorkflowInstance.uuid that domain models expose (e.g.
 * Holding.workflow_instance_uuid, Claim.workflow_instance_uuid).
 */

export type WorkflowApplicability = 'CREATION' | 'STATE' | 'GLOBAL'
export type WorkflowActor = 'ADMIN' | 'CONSUMER' | 'SYSTEM'
export type WorkflowTrigger = 'USER' | 'TIMER' | 'WEBHOOK' | 'SYSTEM' | 'EVENT'

export interface WorkflowState {
  uuid: string
  key: string
  label: string
  is_terminal: boolean
  is_initial: boolean
  color: string
  order: number
  editable_fields: string[]
}

export interface WorkflowAction {
  uuid: string
  key: string
  label: string
  icon: string
  applicability: WorkflowApplicability
  actor: WorkflowActor
  trigger: WorkflowTrigger
  source_state_key: string | null
  target_state_key: string | null
  ui_hints: Record<string, unknown>
  exception_binding: ExceptionActionBinding | null
  capture_fields: CaptureFieldSpec[]
  reason_catalog_key: string
  // Frontend-safe slice of governance_config - drives whether the
  // action dialog renders a reason field and what the minimum length
  // is. Server-side governance_config remains the source of truth.
  requires_reason: boolean
  reason_min_length: number
  // Polymorphic list of UI elements the engine wants the caller to
  // render before the action runs (acknowledgement, checklist,
  // briefing, attachment, …). Each spec is identified by ``kind`` so
  // new precondition types can be added server-side without a UI
  // schema bump. ``blocking=true`` items must be satisfied before the
  // action will execute; ``blocking=false`` items are informational.
  preconditions: PreconditionSpec[]
  order: number
}

/**
 * Generic precondition spec attached to a WorkflowAction.
 *
 * The shape mirrors the polymorphic JSON list stored on
 * ``WorkflowAction.preconditions``. Each entry has a ``kind``
 * discriminator that the UI uses to pick a renderer:
 *
 * * ``acknowledgement`` - a single checkbox the caller must tick
 *   ("I have read the policy"). ``display_payload.text`` is the
 *   prompt copy.
 * * ``checklist`` - a list of items the caller must tick through;
 *   ``display_payload.items`` is an array of ``{ key, label }``.
 * * ``briefing`` - read-only document/markdown body to show inline;
 *   ``display_payload`` carries ``title`` and ``body``.
 * * ``attachment`` - caller must upload at least one file matching
 *   ``display_payload.accept`` before submitting.
 *
 * ``blocking=true`` items must be satisfied (their ``result`` keyed
 * by ``key`` posted back in ``captured_values.preconditions``)
 * before the engine accepts the action. ``blocking=false`` items
 * are informational and never gate submission.
 */
export interface PreconditionSpec {
  key: string
  kind: 'acknowledgement' | 'checklist' | 'briefing' | 'attachment' | string
  blocking: boolean
  display_payload: Record<string, unknown>
}

export interface CaptureFieldSpec {
  key: string
  label?: string
  type?:
    | 'text'
    | 'number'
    | 'boolean'
    | 'date'
    | 'datetime'
    | 'select'
    | 'textarea'
    | 'entity-search'
    | 'file'
    | 'picture'
  required?: boolean
  options?: Array<{ value: string; label: string }>
  placeholder?: string
  help_text?: string
  /**
   * Optional prefill from the attribute's ``configuration_data.default``.
   * The special value ``"today"`` on a date field resolves to the current
   * date; any other value is used verbatim as the initial input value.
   */
  default?: string
  /**
   * ENTITY_SEARCH picker config, forwarded verbatim from the attribute's
   * ``configuration_data``. ``lookup_provider`` selects a live resource
   * provider (dealers, inspectors, …); ``provider_params`` scope its search;
   * ``allow_create`` permits a free-text non-member entry.
   */
  lookup_provider?: string
  provider_params?: Record<string, unknown>
  allow_create?: boolean
  /** DATE / DATE_TIME: reject dates before today (future-only scheduling). */
  disable_past?: boolean
  /** DATE / DATE_TIME: reject dates after today (past-only capture). */
  disable_future?: boolean
  /**
   * FILE / DOCUMENT / IMAGE / VIDEO: allow more than one file, forwarded from
   * the attribute's ``configuration_data.file_multiple``. When true the shared
   * FileUploader captures multiple files (e.g. "Submit Documents"); otherwise a
   * single file replaces on each pick. (WPS-88.)
   */
  multiple?: boolean
  /**
   * FILE / DOCUMENT / IMAGE / VIDEO: upload use case from the attribute's
   * ``configuration_data.file_use_case`` (e.g. ``CLAIM_EVIDENCE``). Drives the
   * accepted MIME types and the upload endpoint.
   */
  file_use_case?: string
}

/**
 * A directed edge in a workflow definition: ``source_key`` -> ``target_key``.
 * ``source_key`` is null for CREATION/global actions with no source state.
 * Exposed on the instance detail so a caller can walk the graph (find the
 * primary track and the branch/outcome states) to render a progress view.
 */
export interface WorkflowTransitionEdge {
  source_key: string | null
  target_key: string
  order: number
}

export interface WorkflowInstance {
  uuid: string
  resource_type: string
  resource_id: string
  current_state: WorkflowState | null
  current_state_key: string
  version: number
  workflow_key: string
  /** Ordered state list of the instance's workflow definition. */
  states?: WorkflowState[]
  /** Directed edges of the workflow definition (source -> target). */
  transitions?: WorkflowTransitionEdge[]
  created_at: string
}

export interface WorkflowActionLog {
  uuid: string
  action_key: string
  from_state_key: string
  to_state_key: string
  initiated_by_type: 'USER' | 'SYSTEM'
  initiated_by_name: string
  initiated_by_system_key: string
  captured_values: Record<string, unknown>
  reason_codes: string[]
  reason_text: string
  result: 'OK' | 'FAILED' | 'DRY_RUN' | 'PENDING_APPROVAL'
  duration_ms: number | null
  correlation_id: string | null
  created_at: string
  error: string
}

export interface PendingTransition {
  uuid: string
  workflow_instance: string
  action_key: string
  action_label: string
  requested_by_name: string
  requested_by_system_key: string
  captured_values: Record<string, unknown>
  reason_codes: string[]
  reason_text: string
  expires_at: string | null
  status: 'PENDING' | 'APPROVED' | 'DENIED' | 'EXPIRED' | 'CANCELLED'
  created_at: string
  denial_reason: string
}

export interface ExecuteActionPayload {
  action_key: string
  captured_values?: Record<string, unknown>
  reason_codes?: string[]
  reason_text?: string
  expected_instance_version?: number
  correlation_id?: string
  dry_run?: boolean
  exception_grant_id?: string
}

export interface ExceptionReason {
  uuid: string
  key: string
  title: string
  description: string
  is_active: boolean
  order: number
  created_at: string
  updated_at: string
}

export interface ExceptionActionBinding {
  uuid: string
  action_key: string
  action_label: string
  resource_type: string
  workflow_key: string | null
  workflow_label: string | null
  workflow_action_uuid: string | null
  suggested_exception_key: string | null
  suggested_exception_title: string | null
  is_active: boolean
  order: number
  created_at: string
  updated_at: string
}

export interface ExceptionActionCatalogEntry {
  uuid: string
  key: string
  label: string
  resource_type: string
  workflow_uuid: string
  workflow_key: string
  workflow_label: string
  source_state_key: string | null
  target_state_key: string | null
  issue_labels: string[]
}

export interface ExceptionGrant {
  uuid: string
  action_key: string
  action_label: string
  reason_codes: string[]
  comment: string
  context: Record<string, unknown>
  entity_key: string
  entity_id: string | null
  granted_by_name: string
  granted_at: string
  correlation_id: string | null
}

export interface ExceptionGrantCreatePayload {
  action_key: string
  reason_codes: string[]
  comment?: string
  workflow_instance?: string | null
  workflow_issue?: string | null
  binding?: string | null
  /** The record the exception is for, when it is not a workflow instance's. */
  entity_id?: string | null
  context?: Record<string, unknown>
  correlation_id?: string | null
}

export interface EntityLogEntry {
  uuid: string
  entity_key: string
  entity_id: string | null
  event_type:
    | 'workflow_action'
    | 'workflow_issue'
    | 'exception_granted'
    | 'attribute_change'
    | 'entity_change'
  action_key: string
  action_label: string
  actor_type: 'USER' | 'SYSTEM'
  user_name: string
  system_actor_key: string
  old_values: Record<string, unknown>
  new_values: Record<string, unknown>
  metadata: Record<string, unknown>
  comment: string
  occurred_at: string
  correlation_id: string | null
}

export interface ExecuteActionResult {
  ok: boolean
  instance: WorkflowInstance | null
  action_log: WorkflowActionLog | null
  pending_transition: PendingTransition | null
  denied_reason: string | null
  denied_message: string | null
  dry_run_plan: Record<string, unknown> | null
  /**
   * Issues raised (or already open) on this resource that block the
   * action. Populated when the engine refuses with ``BlockedByIssue``
   * or a side effect opened a fresh issue mid-attempt.
   */
  issues: WorkflowIssue[]
  /**
   * Preconditions the engine could not auto-satisfy. The dialog
   * inspects this list when ``ok === false`` to render the
   * acknowledgement / checklist / briefing UI inline before the
   * caller resubmits.
   */
  pending_preconditions: PendingPreconditionDisplay[]
}

export interface PendingPreconditionDisplay {
  key: string
  kind: string
  blocking: boolean
  display_payload: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// WorkflowIssue
// ---------------------------------------------------------------------------

/**
 * Generic blocker raised by a workflow action's blockers or raise_issue flow.
 *
 * Mirrors ``WorkflowIssueSerializer`` on the backend. The shape is
 * resource-agnostic - every consumer (registrations, claims, …)
 * resolves issues through the same ``/workflows/issues/<uuid>/``
 * endpoints, the engine handles status bookkeeping.
 */
export type WorkflowIssueStatus = 'OPEN' | 'RESOLVED' | 'DISMISSED'

export type WorkflowIssueResolutionKind =
  | 'exception_granted'
  | 'remediated'
  | 'withdrawn'

/** Raised by a transfer that overrode a blocking rule; decided on the transfer, not the registration. */
export const TRANSFER_ELIGIBILITY_ISSUE_KIND = 'transfer_eligibility'

export interface WorkflowIssue {
  uuid: string
  workflow_instance_uuid: string
  resource_type: string
  /** The record type in the tenant's own word (the paged Exceptions list). */
  resource_type_label?: string
  resource_id: string
  raised_by_action_key: string | null
  raised_by_action_label: string | null
  predicate_key: string
  issue_kind: string
  summary: string
  detail: string
  status: WorkflowIssueStatus
  raised_by_user_name: string
  raised_by_system_key: string
  resolved_by_name: string
  resolved_at: string | null
  resolution_kind: WorkflowIssueResolutionKind | ''
  resolution_text: string
  reason_codes: string[]
  reason_options: WorkflowIssueReasonOption[]
  captured_values: Record<string, unknown>
  policy_snapshot: Record<string, unknown>
  context: WorkflowIssueContext | null
  correlation_id: string | null
  created_at: string
  updated_at: string
}

/**
 * Subject-led context that the WorkflowIssue serializer derives from
 * the resource adapter (subject + meta_sections) plus a generic trigger
 * panel built off the issue's policy_snapshot / summary / detail. The
 * resolve modal uses this to render "what is blocked" as the headline,
 * not "which rule fired".
 */
export interface WorkflowIssueContextSubject {
  kind: string
  title: string
  subtitle: string
}

export interface WorkflowIssueContextMetaSection {
  label: string
  primary: string
  secondary?: string
  tags?: string[]
}

export interface WorkflowIssueContextTriggerBreakdownItem {
  label: string
  value: string
}

export interface WorkflowIssueContextTrigger {
  title: string
  description: string
  breakdown: WorkflowIssueContextTriggerBreakdownItem[]
}

export interface WorkflowIssueReasonOption {
  code: string
  label: string
  /**
   * Optional one-line plain-English explanation of when this exception
   * applies. Rendered as the small secondary line under the label in
   * the resolve modal's reason picker, in place of the raw snake_case
   * code. Backend adapters may populate it from the policy snapshot's
   * reason catalog; falls back to a humanised version of the code.
   */
  description?: string
}

export interface WorkflowIssueContext {
  subject: WorkflowIssueContextSubject
  meta_sections: WorkflowIssueContextMetaSection[]
  trigger: WorkflowIssueContextTrigger
  record_number?: string
  property_address?: string
  assigned_to_name?: string
}

export interface WorkflowIssueResolvePayload {
  resolution_kind?: 'exception_granted' | 'remediated'
  resolution_text?: string
  reason_codes?: string[]
  captured_values?: Record<string, unknown>
}

export interface WorkflowIssueDismissPayload {
  resolution_text?: string
}

export interface WorkflowIssueListParams {
  status?: WorkflowIssueStatus | WorkflowIssueStatus[]
  issue_kind?: string
  predicate_key?: string
  resource_type?: string
  resource_id?: string
  workflow_instance?: string
  page_size?: number
  /** The Exceptions list's page (asks for the paged response). */
  page?: number
  search?: string
  /** Filters panel: any of these record types / reasons, and a raised preset. */
  record_type?: string[]
  reason?: string[]
  date_preset?: string
}
